import { useEffect, useState } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { ArrowLeft, CheckCircle, Loader, Clock, ChevronDown } from "lucide-react";
import { groundOpsApi } from "@/api";

const DEPARTMENTS = [
  { id: "ramp_agent",          label: "Ramp Agent",          color: "text-orange-400 border-orange-500/30 bg-orange-500/10" },
  { id: "baggage_handler",     label: "Baggage Handling",    color: "text-blue-400 border-blue-500/30 bg-blue-500/10"   },
  { id: "fueling_coordinator", label: "Fueling",             color: "text-amber-400 border-amber-500/30 bg-amber-500/10" },
  { id: "cleaning_staff",      label: "Cleaning",            color: "text-teal-400 border-teal-500/30 bg-teal-500/10"   },
  { id: "catering_staff",      label: "Catering",            color: "text-purple-400 border-purple-500/30 bg-purple-500/10" },
  { id: "supervisor",          label: "Supervision",         color: "text-red-400 border-red-500/30 bg-red-500/10"      },
  { id: "dispatcher",          label: "Dispatch",            color: "text-slate-300 border-slate-500/30 bg-slate-700/30" },
];

function initials(name) {
  return name.split(" ").map(w => w[0]).join("").slice(0, 2).toUpperCase();
}
function fmtTime(iso) {
  if (!iso) return "—";
  return new Date(iso).toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit", hour12: false, timeZone: "Asia/Kolkata" });
}

export default function RampStaffDuties({ user }) {
  const { staffId }    = useParams();
  const navigate       = useNavigate();
  const location       = useLocation();

  const staffMember = location.state?.staffMember;

  // Load saved assignment for this staff member
  const savedKey = `shift_assignments_${user.station}`;
  const getSaved = () => {
    try { return JSON.parse(sessionStorage.getItem(savedKey) || "{}"); } catch { return {}; }
  };
  const saved = getSaved()[staffId] || {};

  const [dept, setDept]         = useState(saved.dept || staffMember?.role || "ramp_agent");
  const [tasks, setTasks]       = useState([]);
  const [loadingTasks, setLoadingTasks] = useState(false);
  const [selected, setSelected] = useState(new Set(saved.taskIds || []));
  const [saving, setSaving]     = useState(false);
  const [done, setDone]         = useState(false);

  // Fetch available tasks when dept changes
  useEffect(() => {
    if (!dept) return;
    setLoadingTasks(true);
    groundOpsApi.getAvailableTasks(user.station, dept)
      .then(setTasks)
      .catch(() => setTasks([]))
      .finally(() => setLoadingTasks(false));
  }, [dept, user.station]);

  function toggle(taskId) {
    setSelected(prev => {
      const next = new Set(prev);
      if (next.has(taskId)) next.delete(taskId);
      else next.add(taskId);
      return next;
    });
  }

  async function save() {
    if (selected.size === 0) return;
    setSaving(true);
    try {
      // Assign each selected task to this staff member
      const staff = staffMember || { name: staffId };
      await Promise.all(
        [...selected].map(taskId => groundOpsApi.assignTask(taskId, staff.name))
      );

      // Persist assignment locally for shift briefing progress
      const all = getSaved();
      all[staffId] = { dept, taskIds: [...selected] };
      sessionStorage.setItem(savedKey, JSON.stringify(all));

      setDone(true);
    } finally { setSaving(false); }
  }

  const deptInfo = DEPARTMENTS.find(d => d.id === dept) || DEPARTMENTS[0];

  // Group tasks by flight
  const tasksByFlight = tasks.reduce((acc, t) => {
    const key = t.flight_number || "—";
    if (!acc[key]) acc[key] = { flight: t.flight_number, route: t.route, stand: t.stand, tasks: [] };
    acc[key].tasks.push(t);
    return acc;
  }, {});

  return (
    <div className="p-4 max-w-sm mx-auto">
      <button onClick={() => navigate(-1)} className="flex items-center gap-2 text-slate-500 text-sm mb-4">
        <ArrowLeft size={16} /> Back to Shift Briefing
      </button>

      {done ? (
        <div className="text-center py-12">
          <CheckCircle size={48} className="text-green-400 mx-auto mb-3" />
          <p className="text-white font-bold text-lg">{selected.size} duties assigned</p>
          <p className="text-slate-500 text-sm mt-1">
            {staffMember?.name || staffId} — {deptInfo.label}
          </p>
          <button
            onClick={() => navigate(-1)}
            className="mt-6 bg-orange-500 hover:bg-orange-400 text-white font-black px-6 py-3 rounded-2xl text-sm transition active:scale-95"
          >
            Next Staff Member →
          </button>
        </div>
      ) : (
        <>
          {/* Staff card */}
          <div className="bg-[#0c1929] rounded-2xl border border-slate-700/30 p-4 mb-5">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-full bg-orange-500/10 border border-orange-500/20 flex items-center justify-center text-sm font-black text-orange-400">
                {initials(staffMember?.name || staffId)}
              </div>
              <div>
                <div className="font-black text-base text-white">{staffMember?.name || staffId}</div>
                <div className="text-[10px] text-slate-500 mt-0.5">Assigning duties for today's shift · {user.station}</div>
              </div>
            </div>
          </div>

          {/* Step 1: Department */}
          <div className="mb-5">
            <div className="flex items-center gap-2 mb-3">
              <span className="w-5 h-5 rounded-full bg-orange-500 text-white text-[10px] font-black flex items-center justify-center flex-shrink-0">1</span>
              <span className="text-sm font-black text-white">Select Department</span>
            </div>
            <div className="grid grid-cols-2 gap-2">
              {DEPARTMENTS.map(d => (
                <button
                  key={d.id}
                  onClick={() => { setDept(d.id); setSelected(new Set()); }}
                  className={`px-3 py-3 rounded-xl border text-left transition-all ${dept === d.id ? d.color : "bg-[#0c1929] border-slate-600/30 text-slate-500"}`}
                  data-testid={`dept-${d.id}`}
                >
                  <div className={`text-[11px] font-bold ${dept === d.id ? "" : "text-slate-400"}`}>{d.label}</div>
                  {dept === d.id && <div className="text-[8px] font-bold uppercase mt-0.5">Selected</div>}
                </button>
              ))}
            </div>
          </div>

          {/* Step 2: Pick tasks */}
          <div className="mb-5">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2">
                <span className="w-5 h-5 rounded-full bg-orange-500 text-white text-[10px] font-black flex items-center justify-center flex-shrink-0">2</span>
                <span className="text-sm font-black text-white">Assign Duties</span>
              </div>
              {selected.size > 0 && (
                <span className="text-[10px] font-bold text-orange-400 bg-orange-500/10 border border-orange-500/30 px-2 py-1 rounded-full">
                  {selected.size} selected
                </span>
              )}
            </div>

            {loadingTasks && (
              <div className="text-center py-6 text-slate-600 animate-pulse text-sm flex items-center justify-center gap-2">
                <Loader size={14} className="animate-spin" />
                Loading {deptInfo.label} tasks...
              </div>
            )}

            {!loadingTasks && tasks.length === 0 && (
              <div className="text-center py-6 text-slate-600 bg-[#0c1929] rounded-xl border border-slate-700/20">
                <p className="text-sm">No available tasks for {deptInfo.label} at {user.station}</p>
                <p className="text-[10px] text-slate-700 mt-1">All may already be assigned</p>
              </div>
            )}

            {!loadingTasks && Object.values(tasksByFlight).map(group => (
              <div key={group.flight} className="mb-3">
                {/* Flight group header */}
                <div className="flex items-center gap-2 px-1 mb-2">
                  <span className="text-[10px] font-black text-orange-400">{group.flight}</span>
                  <span className="text-[10px] text-slate-600">{group.route}</span>
                  {group.stand && <span className="text-[10px] text-slate-600">· Stand {group.stand}</span>}
                </div>

                <div className="space-y-1.5">
                  {group.tasks.map(t => {
                    const isSelected = selected.has(t.id);
                    return (
                      <button
                        key={t.id}
                        onClick={() => toggle(t.id)}
                        className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl border transition-all text-left
                          ${isSelected
                            ? "bg-orange-500/10 border-orange-500/40"
                            : "bg-[#0c1929] border-slate-600/20 hover:border-slate-500/40"}`}
                        data-testid={`duty-task-${t.id?.slice(0,6)}`}
                      >
                        {/* Checkbox */}
                        <div className={`w-5 h-5 rounded-md border-2 flex items-center justify-center flex-shrink-0 transition-all
                          ${isSelected ? "bg-orange-500 border-orange-500" : "border-slate-600 bg-transparent"}`}>
                          {isSelected && <CheckCircle size={12} className="text-white" strokeWidth={3} />}
                        </div>

                        <div className="flex-1 min-w-0">
                          <div className={`text-sm font-semibold ${isSelected ? "text-white" : "text-slate-300"}`}>{t.name}</div>
                          <div className="flex items-center gap-1.5 text-[9px] text-slate-600 mt-0.5">
                            <Clock size={8} />
                            <span>{fmtTime(t.planned_time)}</span>
                            {t.vendor && t.vendor !== "—" && <><span>·</span><span>{t.vendor}</span></>}
                            {t.assigned_to && t.assigned_to !== staffMember?.name && (
                              <><span>·</span><span className="text-amber-600">Assigned to {t.assigned_to}</span></>
                            )}
                          </div>
                        </div>
                      </button>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>

          {/* Save button */}
          <button
            onClick={save}
            disabled={saving || selected.size === 0}
            className="w-full py-4 bg-orange-500 hover:bg-orange-400 disabled:opacity-40 text-white font-black text-base rounded-2xl transition-all active:scale-95 sticky bottom-4"
            data-testid="save-duties-btn"
          >
            {saving
              ? <Loader size={18} className="animate-spin mx-auto" />
              : selected.size > 0
                ? `Confirm ${selected.size} Dut${selected.size !== 1 ? "ies" : "y"} → Next Staff`
                : "Select duties to assign"}
          </button>
        </>
      )}
    </div>
  );
}
